import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserFormData, UserFormSchema } from '../../types/form';
import { UsersDetail } from '../../types/settings/UsersTypes';
import { toastMessage } from '@/utils/toast-message';
import { updateUser } from '@/actions/data-fetch-users';
import api from '@/utils/api';
import Cookies from 'js-cookie';
import {
  User,
  Mail,
  Phone,
  Bell,
  ShieldCheck,
  Pause,
  CheckCircle,
  Camera,
  Loader2
} from 'lucide-react';

interface ProfilePageProps {
  onClose?: () => void;
}

const ProfilePage = ({ onClose }: ProfilePageProps) => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<UsersDetail | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [isUploadingPic, setIsUploadingPic] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
    setValue,
    watch,
    reset
  } = useForm<UserFormData>({
    resolver: zodResolver(UserFormSchema),
    defaultValues: {
      userName: '',
      userEmail: '',
      userPhone: '',
      role_uuid: '',
      leadNotifications: [],
      notificationsPaused: 'no',
      dailyEmailReports: []
    },
  });

  // Watch for changes in notification fields
  const leadNotifications = watch('leadNotifications');
  const notificationsPaused = watch('notificationsPaused');

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      // Use the new /me endpoint that gets user from token
      const response = await api.req({
        url: '/users/me',
        method: api.Method.GET,
      });

      if (response.status === 'success' && response.data) {
        const user = (response.data as { data: UsersDetail }).data;
        setUserData(user);

        // Set profile picture
        if (user.profile_pic && user.profile_pic !== 'user.png') {
          setProfilePic(user.profile_pic);
        }

        // Populate form
        setValue('userName', user.userName || '');
        setValue('userEmail', user.userEmail || '');
        setValue('userPhone', String(user.phone || ''));
        setValue('role_uuid', user.role?.uuid || '');

        // Populate notifications
        const notifications: string[] = [];
        if (user.email_notification) notifications.push('email');
        if (user.whatsapp_notifications) notifications.push('whatsapp');
        setValue('leadNotifications', notifications);

        setValue('notificationsPaused', user.user_pause ? 'yes' : 'no');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toastMessage('Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePicClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toastMessage('Please select an image file', 'error');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toastMessage('Image size should be less than 2MB', 'error');
      return;
    }

    setIsUploadingPic(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = Cookies.get('crm_auth_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/users/profile-picture`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: formData,
        }
      );

      const result = await response.json();

      if (result.status === 'success' && result.data?.url) {
        setProfilePic(result.data.url);
        toastMessage('Profile picture updated successfully', 'success');
      } else {
        throw new Error(result.message || 'Failed to upload profile picture');
      }
    } catch (error: any) {
      console.error('Error uploading profile picture:', error);
      toastMessage(error.message || 'Failed to upload profile picture', 'error');
    } finally {
      setIsUploadingPic(false);
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const onSubmit = async (formData: UserFormData) => {
    if (!userData?.uuid) return;

    setIsSubmitting(true);
    try {
      const apiData = {
        userName: formData.userName,
        userEmail: formData.userEmail,
        phone: formData.userPhone,
        role_uuid: formData.role_uuid,
        email_notification: formData.leadNotifications.includes('email'),
        sms_notification: false, // SMS no longer supported
        whatsapp_notifications: formData.leadNotifications.includes('whatsapp'),
        user_pause: formData.notificationsPaused === 'yes',
      };

      const response = await updateUser(userData.uuid, apiData);

      if (response.status === 'success') {
        toastMessage('Profile updated successfully', 'success');
        reset(formData); // Reset form to new values
        onClose?.();
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to update profile', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brandColor"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Profile Header with Picture */}
      <div className="bg-brandColor rounded-t-lg p-6 text-white">
        <div className="flex items-center space-x-4">
          {/* Profile Picture */}
          <div className="relative">
            <div
              onClick={handleProfilePicClick}
              className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center cursor-pointer overflow-hidden hover:bg-white/30 transition-colors"
            >
              {isUploadingPic ? (
                <Loader2 className="h-8 w-8 animate-spin" />
              ) : profilePic ? (
                <img
                  src={profilePic}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="h-10 w-10" />
              )}
            </div>
            <button
              type="button"
              onClick={handleProfilePicClick}
              disabled={isUploadingPic}
              className="absolute bottom-0 right-0 bg-white text-brandColor p-1.5 rounded-full shadow-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <Camera className="h-4 w-4" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{userData?.userName || 'User Profile'}</h1>
            <p className="text-white/80">{userData?.userEmail}</p>
            {userData?.role && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 mt-2">
                <ShieldCheck className="h-3 w-3 mr-1" />
                {userData.role.role_name}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Form Content - Single Page (No Tabs) */}
      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-b-lg shadow-sm">
        <div className="p-6 space-y-8">
          {/* Profile Information Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-brandColor" />
              Profile Information
            </h2>
            <div className="space-y-6 ml-7">
              {/* Name Field */}
              <div>
                <label htmlFor="userName" className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4 mr-2 text-gray-400" />
                  Full Name
                </label>
                <input
                  type="text"
                  id="userName"
                  {...register('userName')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all"
                  placeholder="Enter your full name"
                />
                {errors.userName && (
                  <p className="mt-1 text-sm text-red-600">{errors.userName.message}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="userEmail" className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Mail className="h-4 w-4 mr-2 text-gray-400" />
                  Email Address
                </label>
                <input
                  type="email"
                  id="userEmail"
                  {...register('userEmail')}
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  placeholder="your.email@example.com"
                />
                {errors.userEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.userEmail.message}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="userPhone" className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Phone className="h-4 w-4 mr-2 text-gray-400" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="userPhone"
                  {...register('userPhone')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all"
                  placeholder="Enter your phone number"
                />
                {errors.userPhone && (
                  <p className="mt-1 text-sm text-red-600">{errors.userPhone.message}</p>
                )}
              </div>

              {/* Role Field (Disabled) */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <ShieldCheck className="h-4 w-4 mr-2 text-gray-400" />
                  Role
                  <span className="ml-2 text-xs text-gray-500">(Assigned by admin)</span>
                </label>
                <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                  {userData?.role?.role_name || 'No role assigned'}
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className="border-gray-200" />

          {/* Notification Settings Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Bell className="h-5 w-5 mr-2 text-brandColor" />
              Notification Settings
            </h2>
            <div className="space-y-6 ml-7">
              {/* Lead Notifications */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-4">
                  <Bell className="h-4 w-4 mr-2 text-gray-400" />
                  Lead Notifications
                </label>
                <div className="space-y-3 ml-6">
                  {['email', 'whatsapp'].map((type) => (
                    <label key={type} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        value={type}
                        checked={leadNotifications?.includes(type) || false}
                        onChange={(e) => {
                          const current = leadNotifications || [];
                          if (e.target.checked) {
                            setValue('leadNotifications', [...current, type]);
                          } else {
                            setValue('leadNotifications', current.filter(n => n !== type));
                          }
                        }}
                        className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
                      />
                      <span className="ml-3 text-sm text-gray-700">
                        {type === 'whatsapp' ? 'WhatsApp Notifications' : 'Email Notifications'}
                      </span>
                    </label>
                  ))}
                  <p className="text-xs text-gray-500 italic">Note: SMS notifications are no longer supported</p>
                </div>
              </div>

              {/* Pause Notifications */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-4">
                  <Pause className="h-4 w-4 mr-2 text-gray-400" />
                  Pause All Notifications
                </label>
                <div className="space-y-3 ml-6">
                  {['no', 'yes'].map((value) => (
                    <label key={value} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value={value}
                        checked={notificationsPaused === value}
                        onChange={() => setValue('notificationsPaused', value as 'yes' | 'no')}
                        className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300"
                      />
                      <span className="ml-3 text-sm text-gray-700">
                        {value === 'yes' ? 'Yes, pause all notifications' : 'No, keep notifications active'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Notification Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircle className={`h-5 w-5 mr-2 ${notificationsPaused === 'yes' ? 'text-yellow-500' : 'text-green-500'}`} />
                  <span className="text-sm font-medium text-gray-700">
                    Current Status:
                  </span>
                  <span className={`ml-2 text-sm ${notificationsPaused === 'yes' ? 'text-yellow-600' : 'text-green-600'}`}>
                    {notificationsPaused === 'yes' ? 'Notifications Paused' : 'Notifications Active'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {isDirty && <span>You have unsaved changes</span>}
            </div>
            <div className="flex space-x-3">
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-brandColor border border-transparent rounded-lg hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSubmitting ? 'Updating...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfilePage;
